import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Controller, { inject as controller } from '@ember/controller';
import { task } from 'ember-concurrency';
import FormValidations from 'cio/utils/form-validations';
import SignupController from 'cio/pods/signup/controller';
import AnalyticsService from 'cio/services/analytics';
import FeaturesService from 'ember-feature-flags/services/features';
import { isEmpty } from '@ember/utils';
import ApiService from 'cio/services/api';
import { ProductApp, productActivationInfo } from 'cio/utils/product-app-info';

type DataCenter =
  | { value: string; label: string }
  | { value: string; label: string; disabled: true; disabledTooltip: string };

const DATA_CENTERS: DataCenter[] = [
  { value: 'us', label: '🇺🇸 United States' },
  { value: 'eu', label: '🇪🇺 Europe' },
];

const Validations = {
  companyName(value: string) {
    if (!value?.trim()) {
      return "Company name can't be blank";
    }

    if ((value?.length ?? 0) > 191) {
      return "Company name can't be longer than 191 characters";
    }

    return true;
  },

  employeeCount(this: CompanyController, value: string) {
    if (this.model.signupType.name !== ProductApp.JOURNEYS) {
      return true;
    }
    if (!value?.trim()) {
      return 'Please select the number of employees in your company';
    }

    return true;
  },

  cdpPlatform(this: CompanyController, value: string) {
    if (this.model.signupType.name !== ProductApp.DATA_PIPELINES) {
      return true;
    }
    if (!value?.trim()) {
      return 'Please select a CDP platform';
    }

    return true;
  },

  cdpPlatformOther: {
    dependentKeys: ['cdpPlatform'],
    validator(this: CompanyController, value: string) {
      if (this.model.signupType.name !== ProductApp.DATA_PIPELINES) {
        return true;
      }

      if (this.signupController.getFormValue('cdpPlatform') !== 'other') {
        return true;
      }

      if (!value?.trim()) {
        return 'Please input a CDP platform';
      }

      return true;
    },
  },

  workPhone(value: string) {
    // phone isn't a required field, but if it is filled in, we ensure that it
    // is formatted correctly.  We don't fully validate the string content,
    // only presence of at least 7 digits within it.
    if (value && value.replace(/\D+/g, '').length < 7) {
      return 'Phone number must have at least 7 digits';
    }

    return true;
  },

  dataCenter(this: CompanyController, value: string) {
    if (isEmpty(value?.trim())) {
      return "Data Center can't be blank";
    }

    return true;
  },
};

export default class CompanyController extends Controller {
  validations!: FormValidations;

  @service analytics!: AnalyticsService;
  @service features!: FeaturesService;
  @service api!: ApiService;
  @controller('signup') signupController!: SignupController;

  _employeeCountItems = productActivationInfo[ProductApp.JOURNEYS].employeeCountOptions;
  _cdpPlatforms = productActivationInfo[ProductApp.DATA_PIPELINES].platformOptions;
  _workPhoneTouched = false;

  product = this.signupController.product;

  readonly dataCenters = DATA_CENTERS;

  declare model: {
    signupType: {
      name: string;
      isJourneys: boolean;
      isDataPipelines: boolean;
    };
    testimonials: Record<string, any>;
  };

  constructor() {
    super(...arguments);
    this.set(
      'validations',
      FormValidations.create({ validations: Validations, context: this, modelKey: 'signupController.formValues' }),
    );
  }

  setDefaults() {
    this.setProperties({
      _workPhoneTouched: false,
    });
  }

  get didAttemptToSubmit() {
    return this._submitTask.last != null;
  }

  _submitTask = task(async (ev: MouseEvent) => {
    ev.preventDefault();
    try {
      await this.validations.validateModel();
    } catch (e) {
      // `validateModel` already found and set validation errors
      // return early to avoid executing real submit logic
      return;
    }

    try {
      let { user } = await this.signupController.signupTask.perform();
      let { signupType } = this.signupController.model;
      this.analytics.track('signup', 'company_info_submitted', {
        signupType: signupType.name,
      });

      this.analytics.alias(user.id);
      this.signupController.trackSignupProgress('signup.company');
    } catch {
      // The parent task shows an error message, so we don't need any error handling here.
    }
  });

  get submitDisabled() {
    return (this.validations.hasErrors && this.didAttemptToSubmit) || this._submitTask.isRunning;
  }

  @action
  onWorkPhoneBlur() {
    this.set('_workPhoneTouched', this._workPhoneTouched || Boolean(this.signupController.formValues.workPhone));
  }

  @action
  onSelectEmployeeCount(value: string) {
    this.signupController.setFormValue('employeeCount', value);
    this.validations.validateAttribute('employeeCount');
  }

  @action
  onSelectCdpPlatform(value: string) {
    this.signupController.setFormValue('cdpPlatform', value);
    this.validations.validateAttribute('cdpPlatform');
  }

  @action
  onSelectDatacenter(value: any) {
    if (!isEmpty(value)) {
      this.signupController.setFormValue('dataCenter', value);
    }
    this.validations.validateAttribute('dataCenter');
  }

  @action
  onValueChange(field: keyof SignupController['formValues'], value: string) {
    this.signupController.setFormValue(field, value);
    this.validations.validateAttribute(field);
  }
}
